import { cn } from '@/shared/lib/utils';
import { Select, type SelectOption } from '@/shared/ui/select';
import type { FilterOption } from '@/shared/hooks/useFilterOptions';

// --- Root ---

interface FilterRootProps {
  children: React.ReactNode;
  className?: string;
}

export function FilterRoot({ children, className }: FilterRootProps) {
  return <div className={cn('gap-phx-2 flex items-center', className)}>{children}</div>;
}

// --- Select ---

interface FilterSelectProps<TId = number> {
  options: FilterOption<TId>[];
  value: TId | null;
  onChange: (value: TId | null) => void;
  placeholder?: string;
  label?: string;
  isLoading?: boolean;
  invalid?: boolean;
  size?: 'sm' | 'md' | 'lg';
  className?: string;
}

export function FilterSelect<TId extends number | string = number>({
  options,
  value,
  onChange,
  placeholder,
  label,
  isLoading,
  invalid,
  size = 'md',
  className,
}: FilterSelectProps<TId>) {
  const selectOptions: SelectOption[] = options.map((opt) => ({
    value: String(opt.id),
    label: opt.label,
    disabled: opt.disabled,
  }));

  return (
    <div className={cn('gap-phx-1 flex flex-col', className)}>
      {label && <label className="text-phx-xs text-phx-text-muted font-medium">{label}</label>}
      <Select
        size={size}
        value={value != null ? String(value) : ''}
        onChange={(e) => {
          const newValue = e.target.value;
          if (!newValue) {
            onChange(null);
          } else {
            onChange((typeof value === 'string' ? newValue : Number(newValue)) as TId);
          }
        }}
        disabled={isLoading}
        invalid={invalid}
        placeholder={placeholder}
        options={selectOptions}
      />
    </div>
  );
}
